import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import logo from '../../assets/logo.png';

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { currentLanguage, changeLanguage } = useLanguage();
    const { t } = useTranslation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLanguageChange = (lang) => {
        changeLanguage(lang);
        setLangDropdownOpen(false);
    };

    const navLinks = [
        { path: '/', text: t('nav.home') },
        { path: '/products', text: t('nav.products') },
        { path: '/about', text: t('nav.about') },
        { path: '/news', text: t('nav.news') },
        { path: '/contact', text: t('nav.contact') },
    ];

    const languages = [
        { code: 'uz', label: "O'zbek", flag: '🇺🇿' },
        { code: 'ru', label: 'Русский', flag: '🇷🇺' },
    ];

    return (
        <header 
            className={`sticky top-0 w-full z-50 transition-all duration-300 ${
                isScrolled 
                    ? 'bg-gradient-to-r from-red-900/95 via-red-800/95 to-red-900/95 backdrop-blur-lg shadow-lg border-b border-red-800/40' 
                    : 'bg-gradient-to-r from-red-900/90 via-red-800/90 to-red-900/90 backdrop-blur-md'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src={logo}
                                alt="Bruce Tank Logo" 
                                className="h-28 sm:h-32 lg:h-36 xl:h-40 w-auto transition-transform duration-200 group-hover:scale-105"
                            />
                            <span className="ml-2 sm:ml-3 text-white font-bold text-sm sm:text-base lg:text-lg xl:text-xl hidden xs:block">
                                Bruce Uzbekistan
                            </span>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                        <nav className="flex items-center space-x-1">
                            {navLinks.map(link => (
                                <NavLink 
                                    key={link.path}
                                    to={link.path} 
                                    className={({ isActive }) => 
                                        `px-3 lg:px-4 py-2 rounded-lg font-medium text-sm lg:text-base transition-all duration-200 ${
                                            isActive 
                                                ? 'text-white bg-white/25 shadow-lg border border-white/20' 
                                                : 'text-white/90 hover:text-white hover:bg-white/15'
                                        }`
                                    }
                                >
                                    {link.text}
                                </NavLink>
                            ))}
                        </nav>
                        
                        {/* Language Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                                className="flex items-center space-x-2 px-3 lg:px-4 py-2 rounded-lg bg-white/15 text-white/90 hover:text-white hover:bg-white/25 transition-colors duration-200"
                            >
                                <span className="text-sm">
                                    {languages.find(lang => lang.code === currentLanguage)?.flag}
                                </span>
                                <span className="text-xs lg:text-sm font-medium hidden lg:block">
                                    {languages.find(lang => lang.code === currentLanguage)?.label}
                                </span>
                                <svg 
                                    className={`w-4 h-4 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {langDropdownOpen && (
                                <div className="absolute top-full right-0 mt-2 w-48 bg-red-900/95 backdrop-blur-lg rounded-lg shadow-xl border border-red-800/40 overflow-hidden">
                                    {languages.map(lang => (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors duration-200 ${
                                                currentLanguage === lang.code
                                                    ? 'bg-white/25 text-white shadow-lg'
                                                    : 'text-white/90 hover:text-white hover:bg-white/15'
                                            }`}
                                        >
                                            <span>{lang.flag}</span>
                                            <span className="font-medium">{lang.label}</span>
                                            {currentLanguage === lang.code && (
                                                <svg className="w-4 h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Menu Button & Language */}
                    <div className="lg:hidden flex items-center space-x-2 sm:space-x-3">
                        {/* Mobile Language Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                                className="flex items-center space-x-1 px-2 sm:px-3 py-2 rounded-lg bg-white/15 text-white/90 hover:text-white transition-colors duration-200"
                            >
                                <span className="text-sm">
                                    {languages.find(lang => lang.code === currentLanguage)?.flag}
                                </span>
                                <svg 
                                    className={`w-3 h-3 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {langDropdownOpen && (
                                <div className="absolute top-full right-0 mt-2 w-32 sm:w-36 bg-red-900/95 backdrop-blur-lg rounded-lg shadow-xl border border-red-800/40 overflow-hidden">
                                    {languages.map(lang => (
                                        <button
                                            key={lang.code}
                                            onClick={() => handleLanguageChange(lang.code)}
                                            className={`w-full flex items-center space-x-2 px-3 py-2 text-left transition-colors duration-200 ${
                                                currentLanguage === lang.code
                                                    ? 'bg-white/25 text-white shadow-lg'
                                                    : 'text-white/90 hover:text-white hover:bg-white/15'
                                            }`}
                                        >
                                            <span>{lang.flag}</span>
                                            <span className="text-sm">{lang.code.toUpperCase()}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <button 
                            onClick={() => setIsOpen(!isOpen)} 
                            className="p-2 rounded-lg bg-white/15 text-white/90 hover:text-white hover:bg-white/25 transition-colors duration-200"
                        >
                            <span className="sr-only">Menuni ochish</span>
                            <div className="w-6 h-6 relative flex flex-col justify-center">
                                <span className={`block w-6 h-0.5 bg-current transform transition-all duration-300 origin-center ${isOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'}`}></span>
                                <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}></span>
                                <span className={`block w-6 h-0.5 bg-current transform transition-all duration-300 origin-center ${isOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'}`}></span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className="lg:hidden border-t border-red-800/40 bg-gradient-to-r from-red-900/95 via-red-800/95 to-red-900/95 backdrop-blur-lg">
                    <div className="px-4 py-3 sm:py-4 space-y-1 sm:space-y-2">
                        {navLinks.map(link => (
                            <NavLink 
                                key={link.path}
                                to={link.path} 
                                onClick={() => setIsOpen(false)}
                                className={({ isActive }) => 
                                    `block px-4 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-colors duration-200 ${
                                        isActive 
                                            ? 'text-white bg-white/25 shadow-lg border border-white/20' 
                                            : 'text-white/90 hover:text-white hover:bg-white/15'
                                    }`
                                }
                            >
                                {link.text}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
